import { cn, Price } from '@divlab/divanui';
import { IconEmptyMessage } from '@divlab/divanui/icons';
import { forwardRef, useCallback, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';

import * as Configurator from '@Stores/Configurator';
import * as HeaderStore from '@Stores/Header';
import Img from '@UI/Img';
import BuyButton from '../BuyButton';
import ProductLike from '../ProductLike';
import useTranslation from '@Queries/useTranslation';
import useMedias from '@Hooks/useMedias';
import styles from './ActionBar.module.css';

import type { CSSProperties } from 'react';
import type { ProductData } from '@Types/Product';
import type { ConfiguratorData } from '../..';
import type { ReviewData } from '@Types/Review';

interface ActionBarProps {
  product: ProductData;
  configurator: ConfiguratorData;
  reviews: ReviewData[];
  inView: boolean;
  isConfigurator: boolean;
  hasConfigurator: boolean;
  image?: string;
  handleClickTop: () => void;
  handleClickReviews: () => void;
  handleClickCharacteristics: () => void;
  handleClickDelivery: () => void;
}
const ActionBar = forwardRef<HTMLDivElement, ActionBarProps>(function ActionBar(
  {
    inView,
    product,
    configurator,
    reviews,
    image,
    isConfigurator,
    hasConfigurator,
    handleClickTop,
    handleClickReviews,
    handleClickCharacteristics,
    handleClickDelivery,
  },
  ref,
) {
  const { t } = useTranslation();
  const { isDesktop, isMobileM } = useMedias();
  const { totalHeight } = HeaderStore.useSizes();
  const { pinned, fixed } = HeaderStore.useMenu();
  const {
    image: constructorImage,
    price,
    name,
    constructorHash,
    id,
    parameterValues,
  } = configurator.configuration;
  const currentPrice = isConfigurator ? price : product.price;
  const hasExpired = currentPrice?.expired > 0;

  const reviewsItem: string | null = useMemo(() => {
    if (!reviews.length) return null;

    const r = reviews.filter((review) => {
      return review.source === 'site';
    });

    const label = t('plural-reviews', r.length);

    return `${r.length} ${label}`;
  }, [reviews, t]);

  const additional = useMemo(
    () =>
      [
        reviewsItem && {
          name: reviewsItem,
          icon: IconEmptyMessage,
          testid: 'pinned-reviews',
          handleClick: handleClickReviews,
        },
        {
          name: t('select-options'),
          testid: 'pinned-choose-fabric',
          handleClick: handleClickTop,
        },
        {
          name: t('characteristics.title'),
          testid: 'pinned-characteristics',
          handleClick: handleClickCharacteristics,
        },
        {
          name: t('deliver-and-assembly'),
          testid: 'pinned-delivery',
          handleClick: handleClickDelivery,
        },
      ].filter(Boolean),
    [
      handleClickCharacteristics,
      handleClickDelivery,
      handleClickReviews,
      handleClickTop,
      reviewsItem,
      t,
    ],
  );

  const additionalMobile = useMemo(
    () => additional.filter((item) => item.name !== t('select-options')),
    [additional, t],
  );

  const additionalList = isDesktop || !hasConfigurator ? additionalMobile : additional;

  const innerTop = useMemo(() => {
    if (fixed && !isMobileM) return totalHeight;
    if (pinned && isMobileM) return totalHeight;

    return 0;
  }, [fixed, isMobileM, totalHeight, pinned]);

  const handleScrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleClick = useCallback(() => {
    if (!isMobileM) return;
    Configurator.changePanelView(false);
    handleScrollToTop();
  }, [isMobileM, handleScrollToTop]);

  return (
    <CSSTransition
      timeout={isMobileM ? 400 : 300}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
      }}
      unmountOnExit
      in={!inView}
    >
      <div
        ref={ref}
        className={cn(styles.inner)}
        style={{ '--element-height': `${innerTop}px` } as CSSProperties}
        onClick={handleClick}
      >
        <div className={styles.container}>
          <div className={styles.info}>
            <Img
              src={constructorImage || image}
              className={styles.image}
              cnImage={styles.cnImage}
              onClick={handleScrollToTop}
            />
            <div className={styles.text}>
              <div
                className={styles.name}
                onClick={handleScrollToTop}
              >{`${product.type} ${name}`}</div>
              <div className={styles.additional}>
                {additionalList.map((item) => {
                  const Icon = item?.icon;
                  return (
                    <span
                      onClick={item?.handleClick}
                      key={item.name}
                      className={styles.linkWrapper}
                      data-testid={item.testid}
                    >
                      {Icon && <Icon className={styles.icon} />}
                      <span className={styles.link}>{item.name}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            <div className={styles.price}>
              {hasExpired && (
                <Price className={styles.priceExpired} price={currentPrice.expired} expired />
              )}
              <Price
                className={styles.priceActual}
                price={currentPrice.actual}
                itemProp='price'
                content={currentPrice.actual.toString()}
              />
            </div>
            <BuyButton
              testid='pinned-add-to-cart'
              className={styles.buyButton}
              cnText={styles.buyButtonText}
            />

            <ProductLike
              product={{
                ...product,
                id,
                constructorParameterValues: parameterValues,
              }}
              className={styles.productLike}
              hash={constructorHash}
              data-testid='pinned-add-to-favorites'
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
});
export default ActionBar;
